import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Animate from '../../ui/Animate';
import Button from '../../ui/Button';
import ButtonsGroup from '../../ui/ButtonsGroup';
import PlusIcon from '../../assets/icons/plus.svg';
import FaqItemRow from './FaqItemRow';
import FaqItemEditForm from './FaqItemEditForm';
import { sortFaqItems } from '../../api/admin/faq';
import { useAppDispatch } from '../../store';
import { setIsModalOpen, setModalContent, setModalContentProps, } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
import { selectItemSortingDirtyCategoryIds, setItemSorting, setItemSortingDirty, upsertItem, } from '../../store/adminSlices/faq';
const FaqItemsSection = ({ category }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    // WR-05: avoid the curried selectIsItemSortingDirtyFor selector — it returns
    // a freshly-constructed arrow function on every invocation, defeating
    // useSelector's `===` equality check and causing each FaqItemsSection to
    // re-render on ANY adminFaq slice mutation (loadStatus, categories, etc.).
    // Reading the dirty-ids array directly limits re-renders to actual changes
    // in itemSortingDirtyCategoryIds (which is the only field this consumer
    // cares about).
    const dirtyCategoryIds = useSelector(selectItemSortingDirtyCategoryIds);
    const isSortingDirty = dirtyCategoryIds.includes(category.uid);
    const [sortedItems, setSortedItems] = useState(category.items);
    const [editingItemUid, setEditingItemUid] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [saveSortingStatus, setSaveSortingStatus] = useState(null);
    const itemRefs = useRef({});
    const animateRef = useRef(null);
    const createFormRef = useRef(null);
    useEffect(() => {
        if (showCreateForm) {
            createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [showCreateForm]);
    // Sync local sortedItems whenever the upstream category prop re-renders
    // with a new items array (e.g. after a successful create / delete / save-
    // order). Mirrors AdminTasksList's resync pattern.
    useEffect(() => {
        setSortedItems(category.items);
    }, [category.items]);
    const moveItem = (index, direction) => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= sortedItems.length) {
            return;
        }
        // FLIP: snapshot DOM positions BEFORE state mutation.
        animateRef.current?.capture();
        setSortedItems((prev) => {
            const next = [...prev];
            [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
            return next;
        });
        const swapped = [...sortedItems];
        [swapped[index], swapped[targetIndex]] = [swapped[targetIndex], swapped[index]];
        // Per-category dirty flag (D-15) — independent of categories reorder
        // and of OTHER categories' items reorder.
        dispatch(setItemSorting({
            categoryUid: category.uid,
            itemUids: swapped.map((i) => i.uid),
        }));
        dispatch(setItemSortingDirty({ categoryUid: category.uid, dirty: true }));
    };
    const handleCreateSaved = (saved) => {
        dispatch(upsertItem({ categoryUid: category.uid, item: saved }));
        setShowCreateForm(false);
    };
    const handleEditSaved = (saved) => {
        dispatch(upsertItem({ categoryUid: category.uid, item: saved }));
        setEditingItemUid(null);
    };
    const handleSaveItemSorting = async () => {
        setSaveSortingStatus('pending');
        try {
            await sortFaqItems(category.uid, sortedItems.map((i) => i.uid));
            dispatch(setItemSortingDirty({ categoryUid: category.uid, dirty: false }));
        }
        catch {
            // 400 item_category_mismatch from Phase 5 should not happen here
            // (we only send UIDs from THIS category's local sortedItems). Any
            // failure falls through to the generic somethingWentWrong modal.
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
        }
        finally {
            // WR-01: only the 'pending' state has observable effect (disables the
            // save button); 'success' / 'error' would be clobbered by this finally
            // block in the same micro-task so they never reach a render. Reset
            // directly to null.
            setSaveSortingStatus(null);
        }
    };
    const openDeleteItemModal = (itemUid) => {
        dispatch(setIsModalOpen(true));
        dispatch(setModalContent(MODAL_CONTENT.confirmDeleteFaqItem.key));
        dispatch(setModalContentProps({ itemUid, categoryUid: category.uid }));
    };
    const isAnyFormOpen = showCreateForm || editingItemUid !== null;
    const isEmpty = sortedItems.length === 0 && !showCreateForm;
    return (_jsxs("div", { className: "faq-items-section", children: [_jsx(ButtonsGroup, { children: _jsx(Button, { label: t('admin.faq.add_item'), icon: _jsx(PlusIcon, {}), iconSize: 'small', onClick: () => setShowCreateForm(true), disabled: isAnyFormOpen }) }), isSortingDirty && (_jsx(ButtonsGroup, { children: _jsx(Button, { label: t('button.save_changes'), onClick: handleSaveItemSorting, disabled: saveSortingStatus === 'pending' }) })), isEmpty && (_jsx("div", { className: "faq-items-empty", children: t('admin.faq.category_empty') })), sortedItems.length > 0 && (_jsx("div", { className: "faq-items-list", children: _jsx(Animate, { refs: itemRefs, ref: animateRef, children: sortedItems.map((item, index) => {
                        if (editingItemUid === item.uid) {
                            return (_jsx("div", { ref: (el) => {
                                    itemRefs.current[item.uid] = el;
                                }, className: "faq-item-row faq-item-row--editing", children: _jsx(FaqItemEditForm, { categoryUid: category.uid, item: item, onCancel: () => setEditingItemUid(null), onSaved: handleEditSaved }) }, item.uid));
                        }
                        return (_jsx(FaqItemRow, { ref: (el) => {
                                itemRefs.current[item.uid] = el;
                            }, item: item, onEdit: () => setEditingItemUid(item.uid), onDelete: () => openDeleteItemModal(item.uid), onMove: (direction) => moveItem(index, direction), disableUp: index === 0, disableDown: index === sortedItems.length - 1 }, item.uid));
                    }) }) })), showCreateForm && (_jsx("div", { ref: createFormRef, children: _jsx(FaqItemEditForm, { categoryUid: category.uid, onCancel: () => setShowCreateForm(false), onSaved: handleCreateSaved }) }))] }));
};
export default FaqItemsSection;
